import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, fmtUsd, fmtTimeAgo, INTENT_COLORS, STATUS_COLORS } from "@/lib/nexus";
import Sparkline from "@/components/Sparkline";
import { Sparkles, Loader2, ArrowLeft } from "lucide-react";

export default function AgentDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get(`/agents/${id}`).then((r) => setData(r.data));
  }, [id]);

  const analyze = async () => {
    setLoading(true);
    setAnalysis(null);
    try {
      const r = await api.post("/analyze/agent", { agent_id: id });
      setAnalysis(r.data.analysis);
    } catch (e) {
      setAnalysis("Analysis unavailable.");
    } finally {
      setLoading(false);
    }
  };

  if (!data) return <div className="p-4 text-nexus-text-2">Loading…</div>;
  const { agent, recent_transactions } = data;
  const color = STATUS_COLORS[agent.status];

  return (
    <div className="p-4 space-y-4">
      <Link to="/wallets" data-testid="back-to-wallets" className="text-[11px] font-mono uppercase tracking-widest text-nexus-text-2 hover:text-nexus-cyan flex items-center gap-1">
        <ArrowLeft size={12} /> BACK TO WALLETS
      </Link>

      <div className="panel p-5 brackets">
        <span className="b1" /><span className="b2" />
        <div className="flex flex-wrap justify-between items-start gap-4">
          <div>
            <div className="flex items-center gap-3">
              <span className="w-3 h-3 rounded-full" style={{ background: color, boxShadow: `0 0 12px ${color}` }} />
              <h1 className="font-rajdhani font-bold text-3xl tracking-tight uppercase">{agent.name}</h1>
              <span className="text-[10px] font-mono uppercase tracking-widest border px-1.5 py-0.5" style={{ borderColor: color, color }}>
                {agent.status}
              </span>
            </div>
            <div className="hash mt-2">{agent.address}</div>
            <div className="text-[12px] font-mono text-nexus-text-2 mt-2">
              <span className="text-nexus-violet">{agent.framework}</span> · <span className="text-nexus-cyan">{agent.chain}</span> · first seen {fmtTimeAgo(agent.first_seen)} ago · last {fmtTimeAgo(agent.last_active)} ago
            </div>
          </div>
          <div className="flex items-center gap-6">
            <div className="text-right">
              <div className="panel-title">Balance</div>
              <div className="metric-hero">{fmtUsd(agent.balance_usd)}</div>
            </div>
            <div className="text-right">
              <div className="panel-title">Risk</div>
              <div className="metric-hero" style={{ color }}>{agent.risk_score}</div>
            </div>
            <div className="text-right">
              <div className="panel-title">Tx Count</div>
              <div className="metric-hero">{agent.tx_count.toLocaleString()}</div>
            </div>
          </div>
        </div>
        <div className="mt-4 flex items-center justify-between">
          <div className="flex gap-2 flex-wrap">
            {agent.intent_tags.map((t) => (
              <span key={t} className="text-[10px] font-mono uppercase tracking-widest border border-nexus-violet/60 text-nexus-violet px-1.5 py-0.5">{t}</span>
            ))}
          </div>
          <Sparkline data={agent.sparkline} color={color} width={200} height={40} />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 panel overflow-auto max-h-[520px]">
          <div className="px-3 py-2 border-b border-nexus-border panel-title"><span className="dot" />Recent A2A Transactions</div>
          <table className="data-table">
            <thead><tr><th>Time</th><th>Direction</th><th>Counterparty</th><th>Intent</th><th>Value</th></tr></thead>
            <tbody>
              {recent_transactions.map((t) => {
                const outgoing = t.from_agent_id === agent.id;
                return (
                  <tr key={t.id} data-testid={`agent-tx-${t.id}`}>
                    <td className="text-nexus-text-2">{fmtTimeAgo(t.timestamp)}</td>
                    <td className={outgoing ? "text-nexus-rose" : "text-nexus-cyan"}>{outgoing ? "→ OUT" : "← IN"}</td>
                    <td className="text-nexus-text">{outgoing ? t.to_name : t.from_name}</td>
                    <td>
                      <span className="text-[10px] uppercase tracking-widest border px-1.5 py-0.5" style={{ borderColor: INTENT_COLORS[t.intent], color: INTENT_COLORS[t.intent] }}>
                        {t.intent_label}
                      </span>
                    </td>
                    <td>{fmtUsd(t.value_usd)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="panel p-4 brackets">
          <span className="b1" /><span className="b2" />
          <div className="flex justify-between items-center">
            <div className="panel-title"><span className="dot" style={{ background: "#9D4EDD", boxShadow: "0 0 8px #9D4EDD" }} />AI Risk Analysis</div>
            <button
              data-testid="agent-analyze-btn"
              onClick={analyze}
              disabled={loading}
              className="text-[10px] font-mono uppercase tracking-widest text-nexus-violet border border-nexus-violet/50 hover:bg-nexus-violet/10 px-2 py-1 flex items-center gap-1 disabled:opacity-50"
            >
              {loading ? <Loader2 size={11} className="animate-spin" /> : <Sparkles size={11} />} Run
            </button>
          </div>
          {!analysis && !loading && (
            <div className="text-[12px] text-nexus-text-2 mt-3">
              Run correlation against declared framework behavior, recent commerce patterns, and known drain / collusion signatures.
            </div>
          )}
          {loading && (
            <div className="flex items-center gap-2 mt-3 text-nexus-violet">
              <Loader2 className="animate-spin" size={14} />
              <span className="text-[11px] font-mono uppercase tracking-widest">Correlating…</span>
            </div>
          )}
          {analysis && (
            <div data-testid="agent-analysis-output" className="mt-3 text-[12px] leading-relaxed text-nexus-text whitespace-pre-line">
              {analysis}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
